import React from 'react';

import classNames from '@utils/classnames';

export interface IListPageProps {
  /** The `PageHeading` for this page. */
  heading: React.ReactNode;
  /** Optional filter panel (see `FilterPanel`) rendered above the table. */
  filters?: React.ReactNode;
  /** The table. It gets the remaining height; pass `className="h-full"` to `Table` so it scrolls internally. */
  children: React.ReactNode;
  /**
   * Draw the filters and the table as one panel: the filters sit in the panel's
   * head and the table runs flush to its edges. Pass `flush` to a `FilterPanel`
   * used here so it doesn't draw a second frame.
   */
  panel?: boolean;
  /** Rendered above the panel, outside of it — banners and the like. */
  banner?: React.ReactNode;
  /** Left side of the footer, e.g. "Totaal aantal orders: 128". */
  summary?: React.ReactNode;
  /** Right side of the footer, normally `<Pagination … />`. */
  pagination?: React.ReactNode;
  className?: string;
}

/**
 * Full-height layout for list pages: page heading, optional filters, a table
 * that fills the remaining viewport height and scrolls internally, and a footer
 * with a summary + pagination. Content has 28px of whitespace left and right.
 */
export const ListPage: React.FC<IListPageProps> = ({
  heading,
  filters,
  children,
  panel,
  banner,
  summary,
  pagination,
  className,
}) => {
  return (
    <div className={classNames('-mb-32 flex h-screen flex-col', className)}>
      {heading}

      <div className="flex min-h-0 flex-1 flex-col gap-4 px-7 py-5">
        {banner}

        {panel ? (
          <div className="card list-panel flex min-h-0 flex-1 flex-col">
            {filters != null && <div className="shrink-0 border-b border-border p-4">{filters}</div>}

            <div className="list-panel-body min-h-0 flex-1">{children}</div>
          </div>
        ) : (
          <>
            {filters}

            <div className="min-h-0 flex-1">{children}</div>
          </>
        )}

        {(summary != null || pagination != null) && (
          <div className="flex shrink-0 items-center justify-between gap-4">
            <div className="min-w-0 text-xs text-fg-muted">{summary}</div>
            {pagination}
          </div>
        )}
      </div>
    </div>
  );
};

export interface IFilterPanelProps {
  children: React.ReactNode;
  /** Number of filters currently applied; shown as "n filters actief". */
  activeCount?: number;
  /** Right side of the bottom row, normally a "Wis alle filters" button. */
  actions?: React.ReactNode;
  /** Drop the card frame and padding — for a panel that already provides them (`ListPage panel`). */
  flush?: boolean;
  className?: string;
}

/** White card holding filter fields in a 4-column grid, labels above the fields. */
export const FilterPanel: React.FC<IFilterPanelProps> = ({ children, activeCount, actions, flush, className }) => {
  return (
    <div className={classNames('grid grid-cols-2 gap-x-3.5 gap-y-3 lg:grid-cols-4', !flush && 'card p-4', className)}>
      {children}

      {(activeCount != null || actions != null) && (
        <div className="col-span-full flex items-center justify-between gap-3 pt-0.5">
          <span className="text-xs text-fg-muted">
            {activeCount != null &&
              (activeCount === 0
                ? 'Geen filters actief'
                : `${activeCount} ${activeCount === 1 ? 'filter' : 'filters'} actief`)}
          </span>
          {actions}
        </div>
      )}
    </div>
  );
};

export interface IFilterFieldProps {
  label: React.ReactNode;
  children: React.ReactNode;
  /** Span multiple grid columns. */
  span?: 1 | 2;
  className?: string;
}

/** One labelled filter control inside a `FilterPanel`. */
export const FilterField: React.FC<IFilterFieldProps> = ({ label, children, span = 1, className }) => {
  return (
    <label className={classNames('flex min-w-0 flex-col gap-1.5', span === 2 && 'col-span-2', className)}>
      <span className="label-text truncate">{label}</span>
      {children}
    </label>
  );
};
